import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getApiV1NotificationMobileAppQueryKey,
  usePatchApiV1NotificationMobileAppNotificationidCancel,
} from '@/shared/api/generated';

/**
 * Hook para cancelar o envio de uma notificação pendente ou agendada.
 *
 * Encapsula o hook gerado pelo Kubb e, ao ter sucesso, invalida a listagem de
 * notificações (garantindo que a grid reflita o novo status) e exibe um alerta
 * de sucesso; em caso de erro, exibe um alerta de falha. Os alertas usam o
 * componente `Alert` do `@ads` via o hook `useAlert`.
 * No backend, o cancelamento cancela os jobs Hangfire dos itens pendentes/
 * agendados e marca a notificação como rascunho.
 *
 * Callbacks específicos da tela (ex.: fechar modal) devem ser passados por
 * chamada via `mutate(vars, { onSuccess })` — eles rodam em conjunto com a
 * invalidação e os alertas definidos aqui.
 *
 * @returns Mutation result do TanStack Query; chamar `mutate({ notificationId })`
 *
 * @example
 * ```tsx
 * const { mutate: cancelNotification, isPending } = useNotificationCancel();
 * cancelNotification({ notificationId: 123 });
 * ```
 */
export function useNotificationCancel() {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('notification');

  return usePatchApiV1NotificationMobileAppNotificationidCancel({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getApiV1NotificationMobileAppQueryKey(),
        });
        alert({
          alertType: 'success',
          title: t('toast.cancelSuccessTitle'),
          description: t('toast.cancelSuccessDescription'),
        });
      },
      onError: () => {
        alert({
          alertType: 'danger',
          title: t('toast.cancelErrorTitle'),
          description: t('toast.cancelErrorDescription'),
        });
      },
    },
  });
}
